import React, { useState, useRef, useCallback } from 'react';

import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';

const IMAGE_PROXY_URL = 'https://kot-image-proxy.coinflipsyndicate.online:8443/39bd83260b149297453aa06d71090d57';

const MIN_SCALE = 0.5;
const MAX_SCALE = 20;
const ZOOM_FACTOR = 1.12;

interface ImageViewerProps {
    url: string;
    label?: string;
    overlay?: React.ReactNode;
}

interface ViewState {
    scale: number;
    tx: number;
    ty: number;
}

const ImageViewer: React.FC<ImageViewerProps> = ({ url, label = 'Скрин', overlay }) => {
    const [imageOpen, setImageOpen] = useState(false);
    const [imageLoading, setImageLoading] = useState(true);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [view, setView] = useState<ViewState>({ scale: 1, tx: 0, ty: 0 });

    const dragging = useRef(false);
    const dragMoved = useRef(false);
    const lastMouse = useRef({ x: 0, y: 0 });
    const imgRef = useRef<HTMLImageElement>(null);

    const proxyUrl = `${IMAGE_PROXY_URL}/image?url=${encodeURIComponent(url)}`;

    const handleOpen = () => {
        setView({ scale: 1, tx: 0, ty: 0 });
        setImageOpen(true);
    };

    const handleWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
        e.preventDefault();
        if (!imgRef.current) return;

        const imgRect = imgRef.current.getBoundingClientRect();

        const ratioX = (e.clientX - imgRect.left) / imgRect.width;
        const ratioY = (e.clientY - imgRect.top) / imgRect.height;

        setView(prev => {
            const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE,
                prev.scale * (e.deltaY < 0 ? ZOOM_FACTOR : 1 / ZOOM_FACTOR)
            ));

            // With transform-origin: center, scaling expands from center
            // so only the offset from center (ratio - 0.5) causes drift
            const baseW = imgRect.width / prev.scale;
            const baseH = imgRect.height / prev.scale;
            const ds = newScale - prev.scale;

            return {
                scale: newScale,
                tx: prev.tx - baseW * ds * (ratioX - 0.5),
                ty: prev.ty - baseH * ds * (ratioY - 0.5),
            };
        });
    }, []);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        if (e.button !== 0) return;
        e.preventDefault();
        dragging.current = true;
        dragMoved.current = false;
        lastMouse.current = { x: e.clientX, y: e.clientY };
    }, []);

    const handleMouseMove = useCallback((e: React.MouseEvent) => {
        if (!dragging.current) return;
        dragMoved.current = true;
        const dx = e.clientX - lastMouse.current.x;
        const dy = e.clientY - lastMouse.current.y;
        lastMouse.current = { x: e.clientX, y: e.clientY };
        setView(prev => ({ ...prev, tx: prev.tx + dx, ty: prev.ty + dy }));
    }, []);

    const handleMouseUp = useCallback((e: React.MouseEvent) => {
        const wasDragging = dragging.current;
        const moved = dragMoved.current;
        dragging.current = false;
        if (!wasDragging) return;
        if (!moved && e.target === e.currentTarget) {
            setImageOpen(false);
        }
    }, []);

    const handleMouseLeave = useCallback(() => {
        dragging.current = false;
    }, []);

    return (
        <>
            <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                    {label}
                </a>
                {imageLoading && <CircularProgress size={16} style={{ position: 'absolute', left: '100%', marginLeft: 8 }} />}
                {imageLoaded && (
                    <img
                        src={proxyUrl}
                        alt="Превью"
                        className="rounded border border-gray-300 cursor-pointer hover:border-blue-400 transition-colors"
                        style={{ position: 'absolute', left: '100%', marginLeft: 8, width: 60, height: 40, objectFit: 'cover', objectPosition: 'right top' }}
                        onClick={handleOpen}
                    />
                )}
            </span>
            <img
                src={proxyUrl}
                alt=""
                style={{ display: 'none' }}
                onLoad={() => { setImageLoading(false); setImageLoaded(true); }}
                onError={() => { setImageLoading(false); setImageLoaded(false); }}
            />

            <Dialog
                open={imageOpen}
                onClose={() => setImageOpen(false)}
                maxWidth={false}
                fullScreen
                PaperProps={{
                    sx: {
                        bgcolor: 'rgba(0, 0, 0, 0.9)',
                        boxShadow: 'none',
                        overflow: 'hidden',
                    }
                }}
            >
                <IconButton
                    onClick={() => setImageOpen(false)}
                    sx={{ position: 'absolute', top: 12, right: 12, color: '#fff', zIndex: 1, backgroundColor: 'rgba(0, 0, 0, 0.85)', borderRadius: '8px', padding: '8px', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.85)' } }}
                >
                    <CloseIcon />
                </IconButton>

                {overlay && (
                    <div style={{
                        position: 'absolute',
                        top: 12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1,
                        color: '#fff',
                        backgroundColor: 'rgba(0, 0, 0, 0.85)',
                        borderRadius: 8,
                        padding: '8px 16px',
                        fontSize: 20,
                        whiteSpace: 'nowrap',
                    }}>
                        {overlay}
                    </div>
                )}

                <div
                    style={{
                        width: '100%',
                        height: '100%',
                        cursor: dragging.current ? 'grabbing' : 'grab',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                    onWheel={handleWheel}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseLeave}
                >
                    <img
                        ref={imgRef}
                        src={proxyUrl}
                        alt="Скриншот"
                        draggable={false}
                        style={{
                            maxWidth: '90vw',
                            maxHeight: '90vh',
                            borderRadius: 8,
                            transform: `translate(${view.tx}px, ${view.ty}px) scale(${view.scale})`,
                            transformOrigin: 'center center',
                            userSelect: 'none',
                        }}
                    />
                </div>
            </Dialog>
        </>
    );
};

export default ImageViewer;
